import type { Customer360Snapshot } from "../../../types/crm";

interface Props {
  feedback: Customer360Snapshot["feedbackHistory"];
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-US", {
    timeZone: "America/New_York",
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function Stars({ rating }: { rating: number }) {
  const full = Math.max(0, Math.min(5, Math.round(rating)));
  return (
    <span aria-label={`${full} out of 5 stars`} className="text-amber-500">
      {"★".repeat(full)}
      <span className="text-gray-300">{"★".repeat(5 - full)}</span>
    </span>
  );
}

/**
 * Lightweight feedback history shown inside the Overview tab. Full list lives
 * at GET /admin/customers/:id/feedback (not yet wired in the UI).
 */
export function FeedbackPreview({ feedback }: Props) {
  return (
    <section className="bg-surface-primary border border-[#E9E9EB] rounded-lg shadow-sm">
      <div className="px-5 py-3 border-b border-[#ECECEC]">
        <h2 className="text-sm font-semibold text-[#202020]">
          In-App Feedback
        </h2>
      </div>
      {feedback.length === 0 ? (
        <div className="px-6 py-8 text-sm text-content-tertiary italic">
          No ratings on file.
        </div>
      ) : (
        <ul className="divide-y divide-border-primary">
          {feedback.slice(0, 5).map((f) => (
            <li key={f.id} className="px-6 py-3 flex items-start gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3 text-sm">
                  <Stars rating={f.rating} />
                  <span className="text-xs text-content-tertiary">
                    {formatDate(f.submittedAt)}
                    {f.page && <> &middot; {f.page}</>}
                  </span>
                </div>
                {f.comment && (
                  <p className="mt-1 text-sm text-content-secondary whitespace-pre-wrap">
                    {f.comment}
                  </p>
                )}
              </div>
            </li>
          ))}
          {feedback.length > 5 && (
            <li className="px-6 py-2 text-xs text-content-tertiary">
              Showing 5 of {feedback.length}
            </li>
          )}
        </ul>
      )}
    </section>
  );
}
